import { useScrollElementIntoView } from "@/common/hooks/html.hooks";
import { cn } from "@/common/utils/css.utils";
import { Button } from "@/ui/cva/Button";
import { setTimeout } from "@wry/context";
import { useTranslation } from "next-i18next";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { ProductDetailsPanel } from "@/components/ProductDetail/ProductDetailsPanel";
import { Body } from "@/ui/typography/Body";
import { useEffect, useRef, useState } from "react";

type ProductDetailsInfoDescriptionProps = {
  product: ProductBySlugNonNullable;
};

export const ProductDetailsInfoDescription = ({
  product,
}: ProductDetailsInfoDescriptionProps) => {
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const descriptionRef = useRef<HTMLSpanElement>(null);

  const { t } = useTranslation();
  const { scroll: scrollElementIntoView } = useScrollElementIntoView({
    block: "center",
  });

  useEffect(() => {
    const description = descriptionRef.current;

    if (description) {
      setShowMoreVisible(description.scrollHeight > description.clientHeight);
    }
  }, []);

  const handleShowMoreClick = () => {
    const nextShowMore = !showMore;
    setShowMore(nextShowMore);

    const description = descriptionRef.current;

    if (!description) {
      return;
    }

    description.classList.toggle("line-clamp-4");

    if (nextShowMore === false) {
      scrollElementIntoView(description);
    }
  };

  return (
    <ProductDetailsPanel title={t("pdp:pdp-product-description")}>
      <div className="flex flex-col space-y-2">
        <div className="flex flex-1 flex-col items-start space-y-4">
          {/* Sellers paste separator runs like "————————" into descriptions.
              Unicode forbids a break between two em-dashes (UAX#14 LB17), so
              such a run is one unbreakable word far wider than this column and
              it pushes the whole page sideways. `break-words` does not help:
              it leaves min-content sizing alone, so the column still stretches.
              Only `overflow-wrap: anywhere` both breaks the run and shrinks
              min-content back to the column width. */}
          <Body
            className="relative line-clamp-4 whitespace-pre-line [overflow-wrap:anywhere]"
            ref={descriptionRef}
          >
            {product.description}
            {product.usage_signs_description && (
              <>
                <br />
                <br />
                {product.usage_signs_description}
              </>
            )}
            <div
              className={cn(
                "pointer-events-none absolute bottom-0 left-0 right-0 top-1/2 hidden bg-gradient-to-b from-transparent to-white",
                showMoreVisible && "block",
                showMoreVisible && showMore && "to-transparent"
              )}
            />
          </Body>
        </div>
        {showMoreVisible && (
          <Button
            appearance="minimal"
            className="self-start px-0"
            onClick={handleShowMoreClick}
          >
            {!showMore
              ? t("common:common-show-more")
              : t("common:common-show-less")}
          </Button>
        )}
      </div>
    </ProductDetailsPanel>
  );
};
